/*
 * Rules of the grant rows of the product form that need no React: which rows Central made itself and are shown
 * read-only, which row reads as forever, what its end column says, and how a row is sent back to the server.
 * The product GET (`ProductAddonDetailsSchema`) carries `isTemporary`, `isFeatureTrial` and `followsMaintenance`;
 * no read carries the forever flag, which is read from the dates (tech spec §6.5, §12.1).
 */

export const EDITIONS_SYNC_SCOPE = 'editions';
export const FOREVER_DURATION = 99;
export const FOREVER_DURATION_TYPE = 'years';

export const isReadOnlyGrant = (addon) => {
	return Boolean(addon && (addon.isTemporary || addon.isFeatureTrial || addon.followsMaintenance));
};

/*
 * A row stored with the 99-year sentinel the server keeps for a forever grant, as the products saved before the
 * forever flag existed (SYN-3458) still are: it reads as forever, not as a license that ends in 2125.
 */
export const hasForeverDuration = (addon) => {
	return Boolean(addon) && Number(addon.duration) === FOREVER_DURATION && addon.durationType === FOREVER_DURATION_TYPE;
};

/*
 * A started row with no end that does not follow the maintenance never ends, and so does a row with the 99-year
 * sentinel. Any other row waiting for its activation has no dates at all, so it cannot be told apart from an
 * ordinary one (D147).
 */
export const readsAsForever = (addon) => {
	if (!addon || addon.followsMaintenance) {
		return false;
	}
	if (hasForeverDuration(addon)) {
		return true;
	}

	return Boolean(addon.startDate && !addon.startOnActivation && addon.endDate === null);
};

export const grantKind = (addon) => {
	if (addon?.isTemporary) {
		return 'Temporary';
	}
	if (addon?.isFeatureTrial || addon?.addonType === 'Trial') {
		return 'Trial';
	}

	return 'License';
};

/*
 * Flags a row from the product GET for the form: a forever row comes up ticked. `wasForever` remembers that, so the
 * save knows when the box was unticked. Rows Central made itself carry no flag; they are sent back as read.
 */
export const prepareLoadedGrant = (addon) => {
	const isForever = !isReadOnlyGrant(addon) && readsAsForever(addon);

	return { ...addon, isForever, wasForever: isForever };
};

export const prepareLoadedGrants = (addons) => {
	return (addons || []).map(prepareLoadedGrant);
};

/*
 * Ticking Forever fixes the row at 99 years, the sentinel the server stores for a forever grant, so the duration
 * fields can be locked and the request still carries a valid duration.
 */
export const applyForever = (addon, isForever) => {
	if (!isForever) {
		return { ...addon, isForever: false };
	}

	return { ...addon, isForever: true, duration: FOREVER_DURATION, durationType: FOREVER_DURATION_TYPE };
};

/*
 * The row as the product POST or PATCH takes it (SYN-3458). The server reads the forever state from `isForever` and
 * ignores `endDate` on a write; the null end date is still sent, as the ticket describes the row. A new row always
 * says whether it is forever. An existing row says so only when the box is ticked, or was ticked when the product
 * loaded and is not any more: the PATCH keeps the stored flag of a row that says nothing, so a forever row waiting
 * for its activation (whose box cannot be read from the dates, D147) stays forever when it is saved untouched. The
 * 99-year duration stays in the row, as the server stores it for a forever grant.
 */
export const toGrantPayload = (addon) => {
	const { isForever, wasForever, ...row } = addon;

	if (row.startOnActivation === '') {
		row.startOnActivation = false;
	}
	if (row.startOnActivation) {
		row.startDate = null;
	}
	if (isReadOnlyGrant(row)) {
		return row;
	}
	if (isForever) {
		row.isForever = true;
		row.endDate = null;
	} else if (!row.id || wasForever) {
		row.isForever = false;
	}

	return row;
};

export const isEditionsLicense = (licenseId, catalog) => {
	const license = (catalog || []).find((item) => { return item.id === licenseId; });

	return license?.syncScope === EDITIONS_SYNC_SCOPE;
};

/*
 * The rows that take part in the overlap check, at their original index so an error lands on the right row: rows
 * of an `editions` license may overlap (the most generous grant wins on the machine) and read-only rows never count.
 */
export const rowsForOverlapCheck = (addons, catalog) => {
	return (addons || []).map((addon) => {
		if (!addon || isReadOnlyGrant(addon) || isEditionsLicense(addon.addonId, catalog)) {
			return null;
		}

		return addon;
	});
};

/*
 * The fields of a grant row the form touches when it opens the row on a refused save, so its errors show at once.
 */
export const GRANT_ROW_FIELDS = ['addonId', 'startDate', 'duration', 'durationType', 'tierId'];

/*
 * The index of the first row the validation refuses, or -1. The overview shows one row's fields at a time, so a
 * refused row may have no field on screen for redux-form to block the save with; the form opens this row instead.
 * A hidden row (renewed and expired, see ProductForm.js) has nowhere to show an error and is skipped, as before.
 */
export const firstInvalidGrantIndex = (errors, addons) => {
	return (addons || []).findIndex((addon, index) => {
		const rowErrors = errors?.[index];

		if (!addon || (addon.isRenewed && addon.isExpired) || !rowErrors) {
			return false;
		}

		return Object.values(rowErrors).some(Boolean);
	});
};
